import ExcelJS from "exceljs/dist/exceljs.min.js";
import { saveAs } from "file-saver";

// ─── helpers ────────────────────────────────────────────────────────────────

const toNumber = (n) => {
  if (n === null || n === undefined || n === "") return null;
  const s = String(n).trim();
  const normalized = s.includes(",") && s.includes(".")
    ? s.replace(/\./g, "").replace(",", ".")
    : s.replace(",", ".");
  const x = Number(normalized);
  return Number.isFinite(x) ? x : null;
};

const fmt2 = (n, kurs = 1) => {
  const x = (toNumber(n) ?? 0) * kurs;
  return x;
};

const getLogoUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  let normalized = String(path).replace(/\\/g, "/");
  normalized = normalized.replace(/^[Cc]:\/Dokumenta\//i, "");
  normalized = normalized.replace(/^\/+/, "");
  return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(normalized)}`;
};

async function fetchImageAsArrayBuffer(url, token) {
  const res = await fetch(url, {
    headers: token ? { Authorization: "Bearer " + token } : undefined,
  });
  if (!res.ok) throw new Error(`Logo fetch failed: ${res.status}`);
  return await res.arrayBuffer();
}

function amountToSerbianWords(amount) {
  if (amount === null || amount === undefined || amount === "") return "";
  const n = Number(String(amount).replace(",", "."));
  if (Number.isNaN(n)) return "";
  const din = Math.floor(Math.abs(n));
  const para = Math.round((Math.abs(n) - din) * 100);
  const ones = ["nula","jedan","dva","tri","četiri","pet","šest","sedam","osam","devet","deset","jedanaest","dvanaest","trinaest","četrnaest","petnaest","šesnaest","sedamnaest","osamnaest","devetnaest"];
  const tens = ["","","dvadeset","trideset","četrdeset","pedeset","šezdeset","sedamdeset","osamdeset","devedeset"];
  const hundreds = ["","sto","dvesta","trista","četiristo","petsto","šeststo","sedamsto","osamsto","devetsto"];
  function twoDigits(x) { return x < 20 ? ones[x] : (tens[Math.floor(x/10)] + (x%10 ? ones[x%10] : "")).trim(); }
  function threeDigits(x) { if (!x) return ""; if (x<100) return twoDigits(x); return (hundreds[Math.floor(x/100)] + (x%100 ? twoDigits(x%100) : "")).trim(); }
  function toWords(x) {
    if (!x) return "nula";
    const parts = [];
    const mil = Math.floor(x/1000000); const th = Math.floor((x%1000000)/1000); const rem = x%1000;
    if (mil) parts.push(mil===1?"milion":threeDigits(mil)+"miliona");
    if (th)  parts.push(th===1?"hiljadu":threeDigits(th)+"hiljada");
    if (rem) parts.push(threeDigits(rem));
    return parts.join("").trim();
  }
  let out = toWords(din) + (din===1?"dinar":"dinara");
  if (para>0) out += "i"+toWords(para)+"para";
  return n<0 ? "minus"+out : out;
}

function parseQrDataUrl(dataUrl) {
  if (!dataUrl) return null;
  const m = dataUrl.match(/^data:(image\/(png|jpeg|jpg));base64,(.+)$/i);
  if (!m) return null;
  return { extension: m[2].toLowerCase() === "jpg" ? "jpeg" : m[2].toLowerCase(), base64: m[3] };
}

// ─── main export ────────────────────────────────────────────────────────────

export async function exportInvoiceToExcel(p, { token, isPredracun } = {}, fileName = "Faktura.xlsx") {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Faktura", { views: [{ showGridLines: false }] });

  // 305 = domaća, 306 = strana; 334 = prodaja vozila (nema transport box)
  const isDomaca   = p.IdPodtip === 305;
  const hasTransport = p.IdGrupa !== 334;

  const Kurs = isDomaca ? (toNumber(p.Kurs) ?? 1) : 1;

  ws.pageSetup = {
    paperSize: 9,
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    scale: 90,
    margins: { left: 0.25, right: 0.25, top: 0.35, bottom: 0.35, header: 0.2, footer: 0.2 },
  };

  ws.columns = [
    { width: 4  }, // A
    { width: 32 }, // B
    { width: 7  }, // C
    { width: 7  }, // D
    { width: 10 }, // E
    { width: 12 }, // F
    { width: 8  }, // G
    { width: 10 }, // H
    { width: 12 }, // I
  ];

  const borderAll = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
  const fillGray  = { type:"pattern", pattern:"solid", fgColor:{ argb:"FFB3B3B3" } };

  const setBorder = (r1, c1, r2, c2) => {
    for (let r=r1; r<=r2; r++)
      for (let c=c1; c<=c2; c++)
        ws.getCell(r,c).border = borderAll;
  };

  const merge = (r1, c1, r2, c2) => {
    for (let r=r1; r<=r2; r++)
      for (let c=c1; c<=c2; c++)
        if (ws.getCell(r,c).isMerged) return;
    ws.mergeCells(r1,c1,r2,c2);
  };

  /* Nije proverena — u PDF-u to kaze vodeni zig, a Excel do sada nije govorio
     nista. Signal se cita iz istog HTML-a koji stize uz podatke: procedura u
     njega ubaci sliku ziga samo kad statusProvere nije 1, pa nema potrebe za
     novim poljem ni za drugim pozivom.
     Zig kao sliku preko cele strane Excel ne peče lepo, pa se umesto toga
     stavlja crvena traka na vrh — vidi se i na ekranu i pri stampi. */
  const nijeProverena = typeof p?.htmlContent === 'string'
    && p.htmlContent.includes('watermark-znak');

  let row = 1;

  if (nijeProverena) {
    merge(row, 1, row, 9);
    const c = ws.getCell(row, 1);
    c.value = 'DOKUMENT NIJE PROVEREN';
    c.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    c.alignment = { horizontal: 'center', vertical: 'middle' };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC62828' } };
    ws.getRow(row).height = 22;
    row += 2;   // prazan red ispod, da traka ne slepi za zaglavlje
  }

  // ── Logo + firma desno ──────────────────────────────────────────────────
  merge(row, 1, row, 6);
  merge(row, 7, row, 9);

  if (isDomaca) {
    ws.getCell(row,7).value =
      `PIB: ${p.PIB??""}\nMB: ${p.MB??""}\nŠifra delatnosti: ${p.SifraDelatnosti??""}\nPDV broj: ${p.PDVbroj??""}`;
  } else {
    ws.getCell(row,7).value =
      `PIB: ${p.PIB??""}\nMB: ${p.MB??""}\nPDV broj: ${p.PDVbroj??""}`;
  }
  ws.getCell(row,7).alignment = { wrapText:true, vertical:"top", horizontal:"left" };
  ws.getCell(row,7).font = { size:10 };
  ws.getRow(row).height = 60;

  try {
    const logoUrl = getLogoUrl(p.Logo);
    if (logoUrl) {
      const buffer = await fetchImageAsArrayBuffer(logoUrl, token);
      const ext = (logoUrl.split(".").pop()||"").toLowerCase();
      const extension = (ext==="jpg"||ext==="jpeg") ? "jpeg" : "png";
      const imageId = wb.addImage({ buffer, extension });
      ws.getRow(1).height = 95;
      ws.addImage(imageId, { tl:{col:0.2,row:0.15}, br:{col:5.2,row:1.55}, editAs:"oneCell" });
    }
  } catch(e) { console.warn("Logo not added:", e); }

  row += 2;

  // ── Horizontalna linija ─────────────────────────────────────────────────
  merge(row, 1, row, 9);
  ws.getCell(row,1).border = { bottom:{style:"medium"} };
  row++;

  // ── Mid header: broj računa + kupac ────────────────────────────────────
  merge(row, 1, row, 5);
  ws.getCell(row,1).value = isDomaca
    ? `${p.IsAvansni ? "Avansni račun broj" : isPredracun ? "Predračun broj" : "Račun broj"}: ${p.RacunBroj??""}`
    : `INVOICE NO.: ${p.RacunBroj??""}`;
  ws.getCell(row,1).font = { bold:true, size:13 };

  merge(row, 6, row, 9);
  ws.getCell(row,6).value = p.KupacNaziv??"";
  ws.getCell(row,6).font = { bold:true, size:12 };
  ws.getCell(row,6).alignment = { horizontal:"center", wrapText:true };
  setBorder(row,6,row,9);
  row++;

  // ── Datum + adresa1 ─────────────────────────────────────────────────────
  merge(row, 1, row, 5);
  ws.getCell(row,1).value = isDomaca
    ? `Datum izdavanja: ${p.DatumIzdavanja??""}  ${p.MestoIzdavanja??""}`
    : `${p.MestoIzdavanja??""}  ${p.DatumIzdavanja??""}`;

  merge(row, 6, row, 9);
  ws.getCell(row,6).value = p.KupacAdresa1??"";
  ws.getCell(row,6).alignment = { horizontal:"center", wrapText:true };
  setBorder(row,6,row,9);
  row++;

  // ── Valuta + adresa2 ────────────────────────────────────────────────────
  merge(row, 1, row, 5);
  ws.getCell(row,1).value = isDomaca
    ? `Datum prometa: ${p.DatumPrometa??""}`
    : `Date of payment: ${p.ValutaPlacanja??""}`;

  merge(row, 6, row, 9);
  ws.getCell(row,6).value = p.KupacAdresa2??"";
  ws.getCell(row,6).alignment = { horizontal:"center", wrapText:true };
  setBorder(row,6,row,9);
  row++;

  // ── Valuta plaćanja (domaća) + PIB/MB kupca ─────────────────────────────
  merge(row, 1, row, 5);
  if (isDomaca) {
    ws.getCell(row,1).value = `Valuta plaćanja: ${p.ValutaPlacanja??""}`;
    merge(row, 6, row, 7);
    ws.getCell(row,6).value = `PIB: ${p.KupacPIB??""}`;
    merge(row, 8, row, 9);
    ws.getCell(row,8).value = `MB: ${p.KupacMB??""}`;
  } else {
    ws.getCell(row,1).value = "";
    merge(row, 6, row, 9);
    ws.getCell(row,6).value = `VAT: ${p.KupacPIB??""}`;
    ws.getCell(row,6).alignment = { horizontal:"center" };
  }
  setBorder(row,6,row,9);
  row += 2;

  // ── QR (samo domaća) ────────────────────────────────────────────────────
  if (isDomaca) {
    try {
      const qr = parseQrDataUrl(p.QrDataUrl);
      if (qr) {
        const qrImageId = wb.addImage({ base64:qr.base64, extension:qr.extension });
        ws.addImage(qrImageId, { tl:{col:2.0,row:3.0}, br:{col:4.9,row:8.2}, editAs:"oneCell" });
      }
    } catch(e) { console.warn("QR not added:", e); }
  }

  // ── Transport box ───────────────────────────────────────────────────────
  if (hasTransport) {
    merge(row,1,row,2); ws.getCell(row,1).value = isDomaca?"CMR:":"CMR:"; ws.getCell(row,1).font={bold:true};
    merge(row,3,row,9); ws.getCell(row,3).value = p.CMR??"";
    setBorder(row,1,row,9); row++;

    const tLines = isDomaca ? [
      ["Datum otpreme:", p.DatumOtpreme??"", "Datum prijema:", p.DatumPrijema??""],
      ["Reg. broj:",     p.RegistarskiBroj??"", "Ref. broj:", p.RefBroj??""],
      ["Količina robe:", p.KolicinaRobe??"",    "Vrsta robe:", p.VrstaRobe??""],
    ] : [
      ["Loading date:", p.DatumOtpreme??"", "Unloading date:", p.DatumPrijema??""],
      ["LKW:",          p.RegistarskiBroj??"", "GOODS:", p.VrstaRobe??""],
      ["Ref. no:",      p.RefBroj??"", "", ""],
    ];

    tLines.forEach(([l1,v1,l2,v2]) => {
      merge(row,1,row,2); ws.getCell(row,1).value=l1; ws.getCell(row,1).font={bold:true};
      merge(row,3,row,4); ws.getCell(row,3).value=v1;
      merge(row,5,row,6); ws.getCell(row,5).value=l2; if(l2) ws.getCell(row,5).font={bold:true};
      merge(row,7,row,9); ws.getCell(row,7).value=v2;
      setBorder(row,1,row,9); row++;
    });
    row++;
  }

  // ── Stavke tabela ───────────────────────────────────────────────────────
  const stavke = Array.isArray(p.Stavke) ? p.Stavke : [];

  if (isDomaca) {
    // 9 kolona
    const headers = ["Rb.","OPIS USLUGE","Kol.","Jm","Cena bez PDV-a","Poreska osnovica","PDV %","PDV","Cena sa PDV-om"];
    headers.forEach((h,i) => {
      const cell = ws.getCell(row, i+1);
      cell.value = h;
      cell.font = { bold:true };
      cell.alignment = { horizontal:"center", vertical:"middle", wrapText:true };
      cell.fill = fillGray;
      cell.border = borderAll;
    });
    row++;

    stavke.forEach((s, idx) => {
      ws.addRow([
        s.rb ?? idx+1,
        s.opis ?? "",
        s.kol ?? "",
        s.jm ?? "",
        fmt2(s.cenaBezPDV, Kurs),
        fmt2(s.poreskaOsnovica, Kurs),
        s.poreskaStopa ?? "",
        fmt2(s.pdv, Kurs),
        fmt2(s.cenaSaPDV, Kurs),
      ]);
      setBorder(row,1,row,9);
      ws.getCell(row,2).alignment = { wrapText:true, vertical:"top" };
      [5,6,8,9].forEach(c => {
        ws.getCell(row,c).numFmt = "#,##0.00";
        ws.getCell(row,c).alignment = { horizontal:"right" };
      });
      row++;
    });

  } else {
    // 3 kolone
    const hCell = (c, v) => {
      const cell = ws.getCell(row, c);
      cell.value = v; cell.font={bold:true};
      cell.alignment={horizontal:"center",vertical:"middle",wrapText:true};
      cell.fill=fillGray; cell.border=borderAll;
    };
    hCell(1, "#"); merge(row,2,row,8); hCell(2,"DESCRIPTION"); hCell(9,"PRICE EUR");
    row++;

    // "We charge you..." red
    const chargeText = p.IdGrupa === 334
      ? "We charge you for the sale of vehicle(s):"
      : "We charge you for transport on the route:";
    merge(row,1,row,8);
    ws.getCell(row,1).value = chargeText;
    ws.getCell(row,1).alignment = { horizontal:"center", wrapText:true };
    ws.getCell(row,9).value = "";
    setBorder(row,1,row,9); row++;

    stavke.forEach((s, idx) => {
      ws.getCell(row,1).value = s.rb ?? idx+1;
      merge(row,2,row,8);
      ws.getCell(row,2).value = s.opis ?? "";
      ws.getCell(row,2).alignment = { wrapText:true, vertical:"top" };
      ws.getCell(row,9).value = toNumber(s.cenaSaPDV) ?? 0;
      ws.getCell(row,9).numFmt = "#,##0.00";
      ws.getCell(row,9).alignment = { horizontal:"right" };
      setBorder(row,1,row,9); row++;
    });

    // StavkaNapomena
    if (p.StavkaNapomena) {
      merge(row,1,row,8);
      ws.getCell(row,1).value = p.StavkaNapomena;
      ws.getCell(row,1).alignment = { wrapText:true, horizontal:"center" };
      ws.getCell(row,9).value = "";
      setBorder(row,1,row,9); row++;
    }
  }

  row++;

  // ── Totali ──────────────────────────────────────────────────────────────
  if (isDomaca) {
    [
      ["OSNOVICA bez PDV-a:", fmt2(p.OsnovicaBezPDV, Kurs)],
      ["Osnovica za PDV:",    fmt2(p.OsnovicaZaPDV,  Kurs)],
      ["Ukupno PDV:",         fmt2(p.PDVUkupno,       Kurs)],
    ].forEach(([label, val]) => {
      merge(row,6,row,8); ws.getCell(row,6).value=label; ws.getCell(row,6).font={bold:true};
      ws.getCell(row,9).value=val; ws.getCell(row,9).numFmt="#,##0.00";
      setBorder(row,6,row,9); row++;
    });
    row++;
  }

  merge(row,6,row,8);
  ws.getCell(row,6).value = isDomaca ? "UKUPNO za naplatu:" : "PAYMENT TOTAL (EUR):";
  ws.getCell(row,6).font = { bold:true };
  ws.getCell(row,9).value = fmt2(p.UkupnoZaNaplatu, Kurs);
  ws.getCell(row,9).numFmt = "#,##0.00";
  setBorder(row,6,row,9); row++;

  // Ukupno RSD + Kurs — za stranu fakturu samo ako je firma iz Srbije (IdDrzava = 1)
  if (!isDomaca && p.IdDrzava === 1) {
    const kursVal = toNumber(p.Kurs) ?? 1;
    merge(row,6,row,8);
    ws.getCell(row,6).value = "Ukupno (RSD):";
    ws.getCell(row,6).font = { bold:true };
    ws.getCell(row,9).value = fmt2(p.UkupnoZaNaplatu, kursVal);
    ws.getCell(row,9).numFmt = "#,##0.00";
    setBorder(row,6,row,9); row++;

    merge(row,6,row,8);
    ws.getCell(row,6).value = "Kurs:";
    ws.getCell(row,6).font = { bold:true };
    ws.getCell(row,9).value = kursVal;
    ws.getCell(row,9).numFmt = "#,##0.0000";
    setBorder(row,6,row,9); row++;
  }

  row++;

  // ── Slovima + Kurs (samo domaća) ────────────────────────────────────────
  if (isDomaca) {
    merge(row,1,row,9);
    ws.getCell(row,1).value = `Slovima: ${amountToSerbianWords(fmt2(p.UkupnoZaNaplatu, Kurs))}`;
    ws.getCell(row,1).alignment = { wrapText:true };
    setBorder(row,1,row,9); row++;

    merge(row,1,row,9);
    ws.getCell(row,1).value = `Kurs: ${p.Kurs??""}`;
    setBorder(row,1,row,9); row++;

    if (p.StavkaNapomena) {
      merge(row,1,row,9);
      ws.getCell(row,1).value = p.StavkaNapomena;
      ws.getCell(row,1).alignment = { wrapText:true };
      setBorder(row,1,row,9); row++;
    }
    row++;
  }

  // ── Napomena ─────────────────────────────────────────────────────────────
  merge(row,1,row,9);
  ws.getCell(row,1).value = isDomaca ? "Napomena:" : "Note:";
  ws.getCell(row,1).font = { bold:true };
  setBorder(row,1,row,9); row++;

  merge(row,1,row+1,9);
  ws.getCell(row,1).value = p.Napomena??"";
  ws.getCell(row,1).alignment = { wrapText:true, vertical:"top" };
  setBorder(row,1,row+1,9); row += 3;

  // ── Footer ───────────────────────────────────────────────────────────────
  merge(row,1,row,9);
  ws.getCell(row,1).value = isDomaca
    ? `${p.PlacanjeTekst??""} ${p.RacunZaUplatu??""}`
    : p.PlacanjeTekst??"";
  ws.getCell(row,1).alignment = { wrapText:true }; row++;

  if (!isDomaca) {
    merge(row,1,row,9);
    ws.getCell(row,1).value = `59: Beneficiary Customer ${p.NazivBanka??""}`;
    row++;
    merge(row,1,row,9);
    ws.getCell(row,1).value = `IBAN: ${p.RacunZaUplatu??""}, SWIFT CODE: ${p.Swift??""}`;
    row++;
    merge(row,1,row,9);
    ws.getCell(row,1).value = `${p.NazivFirme??""}, ${p.AdresaFirme??""}`;
    row++;
  }

  merge(row,1,row,9);
  ws.getCell(row,1).value = p.SudTekst??"";
  ws.getCell(row,1).alignment = { wrapText:true }; row += 2;

  // ── Potpisi ──────────────────────────────────────────────────────────────
  const sign1 = isDomaca ? "Pregledao:" : "Inspected by:";
  const sign2 = isDomaca ? "Fakturisao:" : "Invoiced by:";

  merge(row,1,row,4); ws.getCell(row,1).value=sign1; ws.getCell(row,1).font={bold:true};
  setBorder(row,1,row+3,4);
  merge(row,6,row,9); ws.getCell(row,6).value=sign2; ws.getCell(row,6).font={bold:true};
  setBorder(row,6,row+3,9);

  merge(row+2,1,row+2,4); ws.getCell(row+2,1).value=p.Pregledao??""; ws.getCell(row+2,1).alignment={horizontal:"center"};
  merge(row+2,6,row+2,9); ws.getCell(row+2,6).value=p.Fakturisao??""; ws.getCell(row+2,6).alignment={horizontal:"center"};

  ws.pageSetup.printArea = `A1:I${row+5}`;

  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    fileName
  );
}
